import { useEffect, useState } from "react";

import {
  Checkbox,
  TextField,
  Select,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  EnrollmentInstitutionFormCard,
  ParentNoteFormCard,
} from "./components";
import { DEFAULT_INSTITUTION_DETAILS } from "./constants";
import {
  editInstitution,
  editInstitutionSchoolYearOther,
  getInstitutionById,
} from "../../api/Institution/InstitutionApi";
import { getSchoolYearList } from "../../api/SchoolYear/SchoolYearApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { Role, ValidationFailure } from "../../models";
import {
  InstitutionEditRequest,
  Institution,
  InstitutionSchoolYearOther,
  InstitutionLanguageModel,
  InstitutionProgram,
} from "../../models/Institution";
import { HierarchicalCodeBook } from "../../models/Shared/HierarchicalCodeBook";
import {
  useGetInstitutionShiftsQuery,
  useGetLanguagesQuery,
  useGetElectiveSubjectsQuery,
} from "../../queries/codeBookQuery";
import { institutionDetailsUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";
import { useAuth } from "../../hooks/auth/useAuth";
import { InstitutionSchoolYearOtherEditRequest } from "../../models/Institution/InstitutionSchoolYearOtherEditRequest";

const ALLOWED_ROLES_TO_EDIT = [
  Role.CAS,
  Role.Helpdesk,
  Role.InstitutionAdministrator,
];

export function InstitutionEdit() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();

  const { currentRole } = useAuth();

  const isAllowedToEdit = ALLOWED_ROLES_TO_EDIT.includes(
    currentRole?.role || 0
  );

  const handleSave = async () => {
    if (
      [Role.CountyAdministrator, Role.CountyLeader].includes(
        currentRole?.role || 0
      )
    ) {
      return await editInstitutionSchoolYearOther(
        mapInstitutionSchoolYearOtherToEditRequest(institutionDetails)
      );
    }

    return await editInstitution(
      mapInstitutionDetailsToEditRequest(institutionDetails)
    );
  };

  const [institutionDetails, setInstitutionDetails] = useState<Institution>(
    DEFAULT_INSTITUTION_DETAILS
  );
  const [errors, setErrors] = useState<ValidationFailure[]>();

  const { data: subjects, refetch: refetchSubjects } =
    useGetElectiveSubjectsQuery(
      {
        forPrivate: institutionDetails.isPrivateInstitution,
        isCds: institutionDetails.cds ?? false,
        isTur: institutionDetails.tur ?? false,
      },
      { skip: [institutionDetails.cds, institutionDetails.tur].includes(null) }
    );

  const { data: languages } = useGetLanguagesQuery();
  const { data: institutionShifts } = useGetInstitutionShiftsQuery();

  useEffect(() => {
    const _setInstitutionDetails = async () => {
      const schoolYearList = await getSchoolYearList();
      const currentSchoolYear = schoolYearList.find((val) => val.isCurrent);

      let results = null;

      if (id && currentSchoolYear) {
        results = await getInstitutionById(+id, currentSchoolYear.id);
      }

      if (results) {
        setInstitutionDetails(results);
        refetchSubjects();
      }
    };
    _setInstitutionDetails();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const mapInstitutionSchoolYearOtherToEditRequest = (
    institutionDetails: Institution
  ): InstitutionSchoolYearOtherEditRequest => {
    return {
      institutionId: institutionDetails.id,
      schoolYearId: institutionDetails.institutionSchoolYearOther?.schoolYearId,
      enrollmentInstitution:
        !!institutionDetails.institutionSchoolYearOther?.enrollmentInstitution,
      countyId: institutionDetails.address?.countyId,
    };
  };

  const mapInstitutionDetailsToEditRequest = (
    institutionDetails: Institution
  ): InstitutionEditRequest => {
    return {
      id: institutionDetails.id,
      programs: institutionDetails.programs,
      electiveSubjectIds: institutionDetails.electiveSubjects.map(
        (val) => val.parentId
      ) as number[],
      mandatoryForeignLanguageIds:
        institutionDetails.mandatoryForeignLanguages.map(
          (val) => val.parentId
        ) as number[],
      foreignLanguageIds: institutionDetails.foreignLanguages?.map(
        (val) => val.parentId
      ) as number[],
      extendedStayClass:
        institutionDetails.institutionSchoolYearOther?.extendedStayClass ??
        false,
      allDayClass:
        institutionDetails.institutionSchoolYearOther?.allDayClass ?? false,
      languageSchoolingModels: institutionDetails.languageSchoolingModels ?? [],
      activeForEnrollment:
        institutionDetails.institutionSchoolYearOther?.activeForEnrollment ??
        false,
      shiftId: institutionDetails.institutionSchoolYearOther?.shift?.id,
      telephoneNumber:
        institutionDetails.institutionSchoolYearOther?.telephoneNumber || "",
      email: institutionDetails.institutionSchoolYearOther?.email || "",
      remark:
        institutionDetails.institutionSchoolYearOther?.remark || undefined,
      enrollmentInstitution:
        !!institutionDetails.institutionSchoolYearOther?.enrollmentInstitution,
    };
  };

  const handleInstitutionProgramSwitchChange = (e: any) => {
    const entityId = e.target.name as number;

    const newPrograms = institutionDetails.programs.map((p) =>
      p.id == entityId ? { ...p, isEnrollmentModule: !p.isEnrollmentModule } : p
    );

    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      programs: newPrograms,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleInstitutionLanguageModelSwitchChange = (e: any) => {
    const id = e.target.name as number;

    const newLanguageModels = institutionDetails.languageSchoolingModels?.map(
      (p) =>
        p.id == id ? { ...p, isEnrollmentModule: !p.isEnrollmentModule } : p
    );

    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      languageSchoolingModels: newLanguageModels ?? [],
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const getInstitutionProgramListItems = (list: InstitutionProgram[]) => {
    if (!list.length) return;

    return (
      <div>
        {list.map((listItem) => (
          <Row>
            <Col md={6}>
              <Typography variant="medium" color="base">
                {listItem.name}
              </Typography>
            </Col>
            <Col md={6}>
              <Checkbox
                name={listItem.id.toString()}
                disabled={!isAllowedToEdit}
                checked={listItem.isEnrollmentModule}
                onChange={handleInstitutionProgramSwitchChange}
              />
            </Col>
          </Row>
        ))}
      </div>
    );
  };
  const getInstitutionLanguageModelsListItems = (
    list: InstitutionLanguageModel[]
  ) => {
    if (!list.length) return;

    return (
      <div>
        {list.map((listItem) => (
          <Row>
            <Col md={3}>
              <Typography variant="medium" color="base">
                {listItem.languageName}
              </Typography>
            </Col>
            <Col md={3}>
              <Typography variant="medium" color="base">
                {listItem.modelName}
              </Typography>
            </Col>
            <Col md={2}>
              <Checkbox
                name={listItem.id.toString()}
                disabled={!isAllowedToEdit}
                checked={listItem.isEnrollmentModule}
                onChange={handleInstitutionLanguageModelSwitchChange}
              />
            </Col>
          </Row>
        ))}
      </div>
    );
  };
  const handleSubjectChange = (e: any) => {
    const newSubjectIds = e.target.value as number[];
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      electiveSubjects:
        subjects
          ?.filter(
            (val) => newSubjectIds.find((v) => val.id === v) !== undefined
          )
          .map((v) => {
            return {
              id: 0,
              parentId: v.id,
              name: v.name,
            } as HierarchicalCodeBook;
          }) ?? [],
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleMandatoryLanguageChange = (e: any) => {
    const newMandatoryLanguageIds = e.target.value as number[];
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      mandatoryForeignLanguages:
        languages
          ?.filter(
            (val) =>
              newMandatoryLanguageIds.find((v) => v === val.id) !== undefined
          )
          .map((v) => {
            return {
              id: 0,
              parentId: v.id,
              name: v.name,
            } as HierarchicalCodeBook;
          }) ?? [],
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleForeignLanguageChange = (e: any) => {
    const newForeignLanguageIds = e.target.value as number[];
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      foreignLanguages:
        languages
          ?.filter(
            (val) =>
              newForeignLanguageIds.find((v) => v === val.id) !== undefined
          )
          .map((v) => {
            return {
              id: 0,
              parentId: v.id,
              name: v.name,
            } as HierarchicalCodeBook;
          }) ?? [],
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleInstitutionShiftChange = (e: any) => {
    const newInstitutionShiftId = Number.parseInt(e.target.value);
    const shift = institutionShifts!.find(
      (v) => v.id === newInstitutionShiftId
    );
    if (shift) {
      const otherData: InstitutionSchoolYearOther = {
        ...institutionDetails!.institutionSchoolYearOther!,
        shift: shift,
      };
      const newInstitutionDetails: Institution = {
        ...institutionDetails,
        institutionSchoolYearOther: otherData,
      };
      setInstitutionDetails(newInstitutionDetails);
      dispatch(setIsEdited());
    }
  };
  const handleExtendedStayClassChanged = (e: any) => {
    const val =
      institutionDetails.institutionSchoolYearOther?.extendedStayClass;
    const otherData: InstitutionSchoolYearOther = {
      ...institutionDetails!.institutionSchoolYearOther!,
      extendedStayClass: !val,
    };
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      institutionSchoolYearOther: otherData,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleAllDayClassChanged = (e: any) => {
    const val = institutionDetails.institutionSchoolYearOther?.allDayClass;
    const otherData: InstitutionSchoolYearOther = {
      ...institutionDetails!.institutionSchoolYearOther!,
      allDayClass: !val,
    };
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      institutionSchoolYearOther: otherData,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleActiveForEnrollmentChanged = (e: any) => {
    const val =
      institutionDetails.institutionSchoolYearOther?.activeForEnrollment;
    const otherData: InstitutionSchoolYearOther = {
      ...institutionDetails!.institutionSchoolYearOther!,
      activeForEnrollment: !val,
    };
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      institutionSchoolYearOther: otherData,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleTelephoneNumberChanged = (e: any) => {
    const val = e.target.value as string;
    const otherData: InstitutionSchoolYearOther = {
      ...institutionDetails!.institutionSchoolYearOther!,
      telephoneNumber: val && val.length > 0 ? val : null,
    };
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      institutionSchoolYearOther: otherData,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleEmailChanged = (e: any) => {
    const val = e.target.value as string;
    const otherData: InstitutionSchoolYearOther = {
      ...institutionDetails!.institutionSchoolYearOther!,
      email: val && val.length > 0 ? val : null,
    };
    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      institutionSchoolYearOther: otherData,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleParentNoteChanged = (value: string) => {
    const otherData: InstitutionSchoolYearOther = {
      ...institutionDetails!.institutionSchoolYearOther!,
      remark: value,
    };

    const newInstitutionDetails: Institution = {
      ...institutionDetails,
      institutionSchoolYearOther: otherData,
    };
    setInstitutionDetails(newInstitutionDetails);
    dispatch(setIsEdited());
  };
  const handleEnrollmentInstitutionChange = (checked: boolean) => {
    setInstitutionDetails((prev) => ({
      ...prev,
      institutionSchoolYearOther: {
        ...prev.institutionSchoolYearOther!,
        enrollmentInstitution: checked,
      },
    }));
    dispatch(setIsEdited());
  };

  return (
    <ContentArea>
      <ToolbarTop
        showBack={"show"}
        pageTitle={t("common.institutionDetails.institutionDetailsInfo")}
      />
      <FormCard cardTitle={t("common.institutionDetails.institutiBasicInfo")}>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.institutionDetails.name")}
              value={institutionDetails.name}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.institutionDetails.code")}
              value={institutionDetails.code}
              readonly
              plaintext
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.institutionDetails.schoolYear")}
              value={institutionDetails.schoolYear?.name}
              readonly
              plaintext
            />
          </Col>
        </Row>
      </FormCard>
      <FormCard
        cardTitle={t("common.institutionDetails.institutionDetailsPrograms")}
      >
        <Row>
          <Col md={6}>
            <Typography variant="small" component="h3">
              {t("common.institutionEdit.programsName")}
            </Typography>
          </Col>
          <Col md={6}>
            <Typography variant="small" component="h3">
              {t("common.institutionEdit.programIsActiveName")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            {getInstitutionProgramListItems(institutionDetails.programs)}
          </Col>
        </Row>
      </FormCard>
      <FormCard
        cardTitle={t("common.institutionDetails.institutionDetailsOther")}
      >
        <Row>
          <Col md={4}>
            <Select
              multiple
              label={t("common.institutionDetails.izborniPredmeti")}
              name={nameof<typeof institutionDetails>("electiveSubjects")}
              disabled={!isAllowedToEdit}
              value={institutionDetails.electiveSubjects.map(
                (val) => val.parentId
              )}
              onChange={handleSubjectChange}
              selectData={codeBookToSelectData(subjects)}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("ElectiveSubjects")
                )?.message
              }
            />
          </Col>
          <Col md={4}>
            <Select
              multiple
              label={t("common.institutionDetails.obavezniStraniJezik")}
              name={nameof<typeof institutionDetails>(
                "mandatoryForeignLanguages"
              )}
              disabled={!isAllowedToEdit || !!institutionDetails.tur}
              value={institutionDetails.mandatoryForeignLanguages.map(
                (val) => val.parentId
              )}
              onChange={handleMandatoryLanguageChange}
              selectData={codeBookToSelectData(languages)}
              errorText={
                errors?.find((e) =>
                  e.propertyName?.startsWith("MandatoryForeignLanguages")
                )?.message
              }
            />
          </Col>
          <Col md={4}>
            <Select
              multiple
              label={t("common.institutionDetails.straniJezici")}
              name={nameof<typeof institutionDetails>("foreignLanguages")}
              disabled={!isAllowedToEdit || !!institutionDetails.tur}
              value={institutionDetails.foreignLanguages?.map(
                (val) => val.parentId
              )}
              onChange={handleForeignLanguageChange}
              selectData={codeBookToSelectData(languages)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t("common.institutionEdit.extendedStayClass")}
              name={
                "institutionDetails.institutionSchoolYearOther.extendedStayClass"
              }
              checked={
                institutionDetails.institutionSchoolYearOther?.extendedStayClass
              }
              onChange={handleExtendedStayClassChanged}
              disabled={!!institutionDetails.cds || !!institutionDetails.tur || !isAllowedToEdit}
            />
          </Col>
          <Col md={4}>
            <Checkbox
              label={t("common.institutionEdit.allDayClass")}
              name={"institutionDetails.institutionSchoolYearOther.allDayClass"}
              checked={
                institutionDetails.institutionSchoolYearOther?.allDayClass
              }
              onChange={handleAllDayClassChanged}
              disabled={!!institutionDetails.cds || !!institutionDetails.tur || !isAllowedToEdit}
            />
          </Col>
          <Col md={4}>
            <Checkbox
              label={t("common.institutionEdit.activeForEnrollment")}
              name={
                "institutionDetails.institutionSchoolYearOther.activeForEnrollment"
              }
              disabled={!isAllowedToEdit}
              checked={
                institutionDetails.institutionSchoolYearOther
                  ?.activeForEnrollment
              }
              onChange={handleActiveForEnrollmentChanged}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8}>
            {getInstitutionLanguageModelsListItems(
              institutionDetails.languageSchoolingModels ?? []
            )}
          </Col>
          <Col md={4}>
            <Select
              label={t("common.institutionEdit.shifts")}
              name={"institutionDetails.institutionSchoolYearOther.shift.id"}
              disabled={!isAllowedToEdit}
              value={institutionDetails.institutionSchoolYearOther?.shift?.id}
              onChange={handleInstitutionShiftChange}
              selectData={codeBookToSelectData(institutionShifts)}
            />
          </Col>
        </Row>
      </FormCard>
      <EnrollmentInstitutionFormCard
        isReligious={institutionDetails.isReligious}
        isMinority={institutionDetails.isMinority}
        enrollmentInstitution={
          institutionDetails.institutionSchoolYearOther?.enrollmentInstitution
        }
        onChange={handleEnrollmentInstitutionChange}
      />
      <ParentNoteFormCard
        value={institutionDetails.institutionSchoolYearOther?.remark || ""}
        onChange={handleParentNoteChanged}
        disabled={!isAllowedToEdit}
      />
      <FormCard
        cardTitle={t("common.institutionDetails.institutionContactDetails")}
      >
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.phone")}
              value={
                institutionDetails.institutionSchoolYearOther
                  ?.telephoneNumber || ""
              }
              disabled={!isAllowedToEdit}
              onChange={handleTelephoneNumberChanged}
              errorText={
                errors?.find((e) => e.propertyName === "TelephoneNumber")
                  ?.message
              }
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.email")}
              value={institutionDetails.institutionSchoolYearOther?.email || ""}
              disabled={!isAllowedToEdit}
              onChange={handleEmailChanged}
              errorText={
                errors?.find((e) => e.propertyName === "Email")?.message
              }
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        redirectUrlSaveOK={institutionDetailsUrl}
        handleSave={handleSave}
        setSaveErrors={setErrors}
      />
    </ContentArea>
  );
}
